/**
 * Formats a caught error for the OPFS worker → client `postMessage` boundary.
 *
 * Kept a PURE function in its own module (no `self`/Worker globals) so it is
 * unit-testable without a Worker scope — `opfsReceiverWorker.ts` can't be
 * imported under Node vitest (it wires `self.onmessage` at module load).
 *
 * DUCK-TYPED on purpose, NOT `instanceof`. A browser-native `DOMException`
 * (the real disk-full `QuotaExceededError` thrown by
 * `FileSystemSyncAccessHandle.write()/truncate()/flush()`) is **not**
 * `instanceof Error` in browser engines — it has its own prototype chain. An
 * `instanceof Error`-only check would drop its name to the generic `"Error"`
 * at the source, defeating `receiveHost`/`receiveResume`'s STORAGE_FULL
 * classification (they need the `"QuotaExceededError"` name to survive the
 * boundary).
 *
 * `instanceof` is also a TESTING trap here: under Node, `DOMException` IS
 * `instanceof Error`, so a Node test using a real `DOMException` would pass
 * even against the buggy check — a false green that hid this exact bug. Reading
 * `name`/`message` structurally works identically in both environments, so the
 * test can simulate the browser shape (a non-`Error` object carrying a `name`)
 * and actually catch a regression.
 */
export function describeWorkerError(err: unknown): { error: string; errorName: string } {
  if (
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    typeof (err as { name: unknown }).name === "string"
  ) {
    const e = err as { name: string; message?: unknown };
    const message = typeof e.message === "string" ? e.message : "";
    return { error: `${e.name}: ${message}`, errorName: e.name };
  }
  return { error: "unknown error", errorName: "Error" };
}

/**
 * Whether `err` is a local-storage QuotaExceededError from OPFS — a direct
 * main-thread DOMException, or a worker-relayed Error whose `.name` was
 * reconstructed from the worker's structured errorName (describeWorkerError above;
 * a DOMException can't be structured-cloned across postMessage). Drives
 * `receiveHost`/`receiveResume`'s STORAGE_FULL classification.
 *
 * NOT the same as `errors.ts#isQuotaExceeded`, which is the SERVER's create-time
 * daily-byte-quota rejection (429 -> QUOTA_EXCEEDED). This one is the RECEIVER's
 * local disk-full (-> STORAGE_FULL). Two distinct quotas, two distinct wire codes.
 */
export function isQuotaExceededError(err: unknown): boolean {
  return (err instanceof DOMException || err instanceof Error) && err.name === "QuotaExceededError";
}
